import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { loadFaceModels, getDescriptor, detectEAR } from "@/lib/faceApi";
import api from "@/lib/api";

// drop = required eye-openness dip, open = min open EAR, frames = warm-up frames, reopen = fraction of baseline to count as reopened
const BLINK = {
  Easy: { drop: 0.03, open: 0.15, frames: 3, reopen: 0.82 },
  Normal: { drop: 0.045, open: 0.18, frames: 5, reopen: 0.9 },
  Strict: { drop: 0.065, open: 0.2, frames: 6, reopen: 0.94 },
};

const TIMEOUT_MS = 25000;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const raf = () => new Promise((r) => requestAnimationFrame(() => r()));

export function FaceScanDialog({ open, onOpenChange, mode = "verify", onResult }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cbRef = useRef({});
  const [status, setStatus] = useState("loading"); // loading | ready | failed | error
  const [blinkDone, setBlinkDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [hud, setHud] = useState({ face: false, ear: 0, base: 0 });
  const [diag, setDiag] = useState(null);
  const isRegister = mode === "register";

  cbRef.current = { onResult, onOpenChange };

  useEffect(() => {
    if (!open) return;
    let running = true;
    let stream = null;
    let cfg = BLINK.Easy;
    api.get("/hris/config/clock-settings").then(({ data }) => { cfg = BLINK[data.blink_strictness] || BLINK.Easy; }).catch(() => {});
    setStatus("loading"); setBlinkDone(false); setBusy(false); setHud({ face: false, ear: 0, base: 0 }); setDiag(null);

    const stopAll = () => {
      running = false;
      if (stream) { stream.getTracks().forEach((t) => t.stop()); stream = null; }
    };

    // Draw the current video frame (center-cropped square) onto the fixed-size canvas.
    // Brightness/contrast boost helps face detection succeed in low light.
    const drawFrame = () => {
      const v = videoRef.current, cv = canvasRef.current;
      if (!v || !cv || !v.videoWidth || !v.videoHeight) return false;
      const s = Math.min(v.videoWidth, v.videoHeight);
      const sx = (v.videoWidth - s) / 2, sy = (v.videoHeight - s) / 2;
      const ctx = cv.getContext("2d");
      ctx.filter = "brightness(1.4) contrast(1.15)";
      ctx.drawImage(v, sx, sy, s, s, 0, 0, cv.width, cv.height);
      ctx.filter = "none";
      return true;
    };

    const compactPhoto = () => {
      const src = canvasRef.current;
      const tmp = document.createElement("canvas");
      let size = 180, q = 0.55, out = src.toDataURL("image/jpeg", 0.5);
      for (let i = 0; i < 6; i++) {
        tmp.width = size; tmp.height = size;
        tmp.getContext("2d").drawImage(src, 0, 0, size, size);
        out = tmp.toDataURL("image/jpeg", q);
        const bytes = Math.ceil((out.length - out.indexOf(",") - 1) * 3 / 4);
        if (bytes <= 11 * 1024) break; // target <= ~11 KB
        if (q > 0.35) q -= 0.1; else size -= 20;
      }
      return out;
    };

    const capture = async () => {
      setBusy(true);
      try {
        let descriptor = null;
        for (let i = 0; i < 4 && !descriptor; i++) {
          if (drawFrame()) descriptor = await getDescriptor(canvasRef.current);
          if (!descriptor) await wait(250);
        }
        if (!descriptor) { setStatus("failed"); setBusy(false); return; }
        const photo = compactPhoto();
        stopAll();
        cbRef.current.onResult && cbRef.current.onResult({ descriptor, photo, liveness: true });
        cbRef.current.onOpenChange && cbRef.current.onOpenChange(false);
      } catch (e) {
        setStatus("failed");
      } finally { setBusy(false); }
    };

    // Liveness loop: blink detected by a DIP in eye-openness (max-min EAR >= threshold),
    // which is robust to cameras/landmark models that never report a fully-closed eye.
    // A static photo keeps EAR ~constant (range ~0) and is still rejected.
    const loop = async () => {
      let baseline = 0, frames = 0, lastFace = 0, earMin = 9, earMax = 0, dipSeen = false;
      const start = Date.now();
      while (running) {
        if (Date.now() - start > TIMEOUT_MS) {
          if (running) { setDiag({ frames, min: Math.round(earMin * 100) / 100, max: Math.round(earMax * 100) / 100 }); setStatus("failed"); }
          return;
        }
        if (drawFrame()) {
          let ear = null;
          try { ear = await detectEAR(canvasRef.current, 224); } catch (e) { /* skip frame */ }
          if (ear != null) {
            lastFace = Date.now();
            frames += 1;
            if (ear > baseline) baseline = ear;
            if (ear < earMin) earMin = ear;
            if (ear > earMax) earMax = ear;
            setHud({ face: true, ear: Math.round(ear * 100) / 100, base: Math.round(baseline * 100) / 100 });
            const ready = frames >= cfg.frames && earMax >= cfg.open;
            if (ready && (earMax - earMin) >= cfg.drop) dipSeen = true; // a blink happened
            if (dipSeen && ear > baseline * cfg.reopen) { // eyes open again -> capture a clean frame
              setBlinkDone(true); await capture(); return;
            }
          } else if (Date.now() - lastFace > 400) {
            setHud((h) => ({ ...h, face: false }));
          }
        }
        await raf();
        await wait(40);
      }
    };

    (async () => {
      try {
        await loadFaceModels();
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 320 }, height: { ideal: 320 } } });
        if (!running) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = videoRef.current;
        v.srcObject = stream;
        await v.play().catch(() => {});
        for (let i = 0; i < 25 && running && !v.videoWidth; i++) await wait(100); // wait for real dimensions (iOS)
        if (!running) return;
        setStatus("ready");
        loop();
      } catch (e) {
        if (running) setStatus("error");
      }
    })();

    return () => stopAll();
  }, [open, attempt]);

  const retry = () => { setStatus("loading"); setBlinkDone(false); setAttempt((a) => a + 1); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="face-scan-dialog">
        <DialogHeader>
          <DialogTitle>{isRegister ? "Register your face" : "Face verification"}</DialogTitle>
          <DialogDescription>Liveness check — look at the camera and <strong>blink</strong> to confirm you're a real person.</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-xl overflow-hidden bg-slate-900 aspect-square flex items-center justify-center">
          <video ref={videoRef} playsInline muted autoPlay className="h-full w-full object-cover scale-x-[-1]" data-testid="face-video" />
          <canvas ref={canvasRef} width={256} height={256} className="hidden" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className={`h-48 w-40 rounded-[50%] border-2 border-dashed ${blinkDone ? "border-emerald-400" : "border-white/70"}`} />
          </div>
          {status === "ready" && !blinkDone && !busy && (
            <div className="absolute bottom-0 inset-x-0 bg-slate-900/80 text-white text-sm py-2.5 flex items-center justify-center gap-2" data-testid="liveness-prompt">
              <Icons.Eye className="h-4 w-4 animate-pulse text-amber-300" /> {hud.face ? "Please blink to verify" : "Center your face · add more light"}
            </div>
          )}
          {status === "ready" && (
            <div className="absolute top-1.5 left-2 text-[10px] font-mono text-white/80 bg-black/40 px-1.5 py-0.5 rounded" data-testid="face-debug">
              {hud.face ? `face ✓  ear ${hud.ear}  base ${hud.base}` : "searching for face…"}
            </div>
          )}
          {busy && <div className="absolute bottom-0 inset-x-0 bg-emerald-900/80 text-white text-sm py-2.5 flex items-center justify-center gap-2" data-testid="liveness-ok"><Icons.CheckCircle2 className="h-4 w-4 text-emerald-300" /> Liveness confirmed — capturing…</div>}
          {status === "loading" && <div className="absolute inset-0 flex items-center justify-center text-sm text-white bg-slate-900/80" data-testid="face-loading"><Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting camera…</div>}
          {status === "error" && <div className="absolute inset-0 flex flex-col items-center justify-center text-sm text-white bg-slate-900/90 p-4 text-center" data-testid="face-error"><Icons.CameraOff className="h-6 w-6 mb-2 text-rose-400" /> Camera unavailable. Allow camera access and try again.</div>}
          {status === "failed" && <div className="absolute inset-0 flex flex-col items-center justify-center text-sm text-white bg-slate-900/90 p-4 text-center gap-3" data-testid="liveness-failed"><div><Icons.ShieldAlert className="h-6 w-6 mb-2 text-amber-400 mx-auto" /> No blink detected. A live person must blink — photos won't pass.{diag && <div className="mt-2 text-[11px] font-mono text-white/70" data-testid="liveness-diag">face frames: {diag.frames} · ear {diag.min}–{diag.max}</div>}</div></div>}
        </div>
        {status === "failed" && <Button onClick={retry} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="liveness-retry"><Icons.RefreshCw className="h-4 w-4 mr-1.5" /> Try again</Button>}
        {status === "error" && <Button onClick={() => onOpenChange(false)} variant="outline" className="w-full">Close</Button>}
      </DialogContent>
    </Dialog>
  );
}
